import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useMemo, useReducer, useState } from 'react';
import { buildConsentCompiledTemplateZip } from '../bundler';
import { defaultConsentActions } from './consent.context.types';
import { consentReducer, initialState } from './consent.reducer';
import { useConsentLibrary } from '../hooks/use_consent_library.hook';
export const ConsentStateContext = createContext({
    state: initialState,
    activeDocument: null,
    consentFiles: [],
    isLoading: true,
});
export const ConsentActionsContext = createContext(defaultConsentActions);
export const ConsentScopeContext = createContext({
    studyId: undefined,
});
const readFileAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(blob);
});
export const ConsentProvider = ({ entityId, studyId, children }) => {
    const [state, dispatch] = useReducer(consentReducer, initialState);
    const [consentFiles, setConsentFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { loadResources, createResource, updateResourceData, archiveResource, getResourcePdfUrl, saveCompiledTemplate, publishTemplate, } = useConsentLibrary(entityId, studyId);
    const loadDocuments = useCallback(async () => {
        setIsLoading(true);
        try {
            const { fileRows, documents } = await loadResources();
            dispatch({ type: 'LOAD_DOCUMENTS', payload: documents });
            setConsentFiles(fileRows);
        }
        finally {
            setIsLoading(false);
        }
    }, [loadResources]);
    const addDocument = useCallback(async (document) => {
        dispatch({ type: 'ADD_DOCUMENT', payload: document });
        await updateResourceData(document.id, {
            elements: document.elements,
            totalPages: document.totalPages,
        });
    }, [updateResourceData]);
    const updateDocument = useCallback(async (document) => {
        dispatch({ type: 'UPDATE_DOCUMENT', payload: document });
        await updateResourceData(document.id, {
            elements: document.elements,
            totalPages: document.totalPages,
        });
    }, [updateResourceData]);
    const markResourceArchived = useCallback((id) => {
        dispatch({ type: 'ARCHIVE_DOCUMENT', payload: id });
        setConsentFiles((prev) => prev.map((file) => (file.id === id ? { ...file, status: 'Archived' } : file)));
    }, []);
    const setActiveDocument = useCallback((documentId) => {
        dispatch({ type: 'SET_ACTIVE_DOCUMENT', payload: documentId });
    }, []);
    const addElement = useCallback((documentId, element) => {
        dispatch({ type: 'ADD_ELEMENT', payload: { documentId, element } });
    }, []);
    const autoMapElements = useCallback((documentId, elements) => {
        dispatch({ type: 'AUTO_MAP_ELEMENTS', payload: { documentId, elements } });
    }, []);
    const updateElement = useCallback((documentId, element) => {
        dispatch({ type: 'UPDATE_ELEMENT', payload: { documentId, element } });
    }, []);
    const deleteElement = useCallback((documentId, elementId) => {
        dispatch({ type: 'DELETE_ELEMENT', payload: { documentId, elementId } });
    }, []);
    const updateDocumentRoles = useCallback((documentId, roles) => {
        dispatch({ type: 'UPDATE_DOCUMENT_ROLES', payload: { documentId, roles } });
    }, []);
    const saveDocumentToStorage = useCallback(async (document) => {
        await updateResourceData(document.id, {
            elements: document.elements,
            totalPages: document.totalPages,
            roles: document.roles,
        });
    }, [updateResourceData]);
    const uploadConsentFile = useCallback(async (file, name, metadata) => {
        const { fileRow, document } = await createResource(file, name, metadata);
        dispatch({ type: 'ADD_DOCUMENT', payload: document });
        setConsentFiles((prev) => [...prev, fileRow]);
    }, [createResource]);
    const archiveTemplateVersion = useCallback(async (id) => {
        const templateKey = consentFiles.find((file) => file.id === id)?.templateKey ?? state.documents.find((doc) => doc.id === id)?.key;
        markResourceArchived(id);
        await archiveResource(id, templateKey);
    }, [archiveResource, consentFiles, markResourceArchived, state.documents]);
    const fetchDocumentPdf = useCallback(async (documentId) => {
        const document = state.documents.find((d) => d.id === documentId);
        if (!document?.assetId) {
            return;
        }
        const pdfUrl = await getResourcePdfUrl(document.assetId);
        if (!pdfUrl) {
            return;
        }
        const response = await fetch(pdfUrl);
        const blob = await response.blob();
        const dataUrl = await readFileAsDataUrl(blob);
        dispatch({ type: 'SET_DOCUMENT_PDF', payload: { documentId, pdfDataUrl: dataUrl } });
    }, [state.documents, getResourcePdfUrl]);
    const saveConsentTemplate = useCallback(async (document, bundle) => {
        const zipBlob = await buildConsentCompiledTemplateZip(bundle);
        await saveCompiledTemplate(document.id, document.name, zipBlob, bundle, {
            elements: document.elements,
            totalPages: document.totalPages,
            roles: document.roles,
        });
    }, [saveCompiledTemplate]);
    const publishConsentTemplate = useCallback(async (document) => {
        await publishTemplate(document);
        dispatch({ type: 'SET_ACTIVE_DOCUMENT', payload: null });
        setConsentFiles((prev) => prev.map((file) => (file.id === document.id ? { ...file, status: 'Published' } : file)));
    }, [publishTemplate]);
    const activeDocument = useMemo(() => state.documents.find((d) => d.id === state.activeDocumentId) ?? null, [state.activeDocumentId, state.documents]);
    const stateContextValue = useMemo(() => ({
        state,
        activeDocument,
        consentFiles,
        isLoading,
    }), [state, activeDocument, consentFiles, isLoading]);
    const actionsContextValue = useMemo(() => ({
        loadDocuments,
        addDocument,
        updateDocument,
        setActiveDocument,
        addElement,
        autoMapElements,
        updateElement,
        deleteElement,
        updateDocumentRoles,
        saveDocumentToStorage,
        uploadConsentFile,
        archiveTemplateVersion,
        fetchDocumentPdf,
        saveConsentTemplate,
        publishConsentTemplate,
    }), [
        loadDocuments,
        addDocument,
        updateDocument,
        setActiveDocument,
        addElement,
        autoMapElements,
        updateElement,
        deleteElement,
        updateDocumentRoles,
        saveDocumentToStorage,
        uploadConsentFile,
        archiveTemplateVersion,
        fetchDocumentPdf,
        saveConsentTemplate,
        publishConsentTemplate,
    ]);
    const scopeContextValue = useMemo(() => ({ studyId }), [studyId]);
    return (_jsx(ConsentScopeContext.Provider, { value: scopeContextValue, children: _jsx(ConsentActionsContext.Provider, { value: actionsContextValue, children: _jsx(ConsentStateContext.Provider, { value: stateContextValue, children: children }) }) }));
};
